import React, { useContext } from 'react';
import Playlist from '../../models/playlist/playlist';
import SyncSource from '../../models/playlist/syncSource';
import {
  findSource,
  linkPlaylistSource,
  unlinkSource,
} from '../../models/playlist/syncSourceHelpers';
import SyncType from '../../models/playlist/syncType';
import { LibraryContext } from '../../services/library/libraryService';
import { Context } from '../../services/_globalContext/context';
import Constants, { Keys, messages } from '../../settings';
import { preventDefaultStopPropogation } from '../../utilities/preventDefault';
import FadilaMenuItem from '../common/contextMenu/fadilaMenuItem';
import FadilaSubMenu from '../common/contextMenu/fadilaSubMenu';
import { showLibraryErrors } from '../../services/library/libraryError';

interface Props {
  /** The playlist's URN - what the API addresses it by. */
  playlistUrn: string;
  title: string;
}

/**
 * "Sync with Playlist" on a SoundCloud playlist's context menu: the user's
 * Fadila playlists, a tick on those already linked to this one (pressing
 * unlinks), and a new playlist named after the SoundCloud one.
 *
 * The YouTube sibling of this asks for write permission at the same time;
 * SoundCloud needs none beyond being signed in, so linking is the whole
 * gesture. Links are two-way by default and the direction is changed
 * afterwards from the Fadila playlist's own menu in the library.
 *
 * Only playlists you own can be written to. Linking someone else's two-way
 * looks fine here and fails on the first push, which the sync reports.
 */
export default function SoundCloudSyncSubMenu({ playlistUrn, title }: Props) {
  const ctx = useContext(Context);
  const libCtx = useContext(LibraryContext);

  function link(pl: Playlist) {
    // The freshest copy, in case the menu was open across an edit.
    const current = libCtx.playlists.find((x) => x.id === pl.id) || pl;

    libCtx.updatePlaylist(
      linkPlaylistSource(current, SyncType.SoundCloudPlaylist, playlistUrn, title),
    );
    ctx.callSnackbar(messages(Keys.soundCloudSyncLinked), false);
  }

  function unlink(pl: Playlist, source: SyncSource) {
    const current = libCtx.playlists.find((x) => x.id === pl.id) || pl;

    libCtx.updatePlaylist(unlinkSource(current, source));
    ctx.callSnackbar(messages(Keys.soundCloudSyncUnlinked), false);
  }

  function linkToNewPlaylist() {
    const name = window.prompt('Please enter or confirm new playlist name', title);

    if (name) {
      showLibraryErrors(ctx.callSnackbar, () => link(libCtx.createPlaylist(name)));
    }
  }

  return (
    <FadilaSubMenu
      title="Sync with Playlist"
      preventCloseOnClick
      onClick={(ev: React.MouseEvent) => ev.stopPropagation()}
    >
      {libCtx.playlists.map((pl) => {
        const source = findSource(pl, SyncType.SoundCloudPlaylist, playlistUrn);

        return (
          <FadilaMenuItem
            key={pl.id}
            onClick={preventDefaultStopPropogation(() => (source ? unlink(pl, source) : link(pl)))}
            attributes={{
              title: source
                ? 'Synced with this SoundCloud playlist. Press to unlink.'
                : 'Keep this playlist in sync with the SoundCloud playlist',
            }}
          >
            {source ? '✓ ' : ''}
            {pl.name}
          </FadilaMenuItem>
        );
      })}
      <FadilaMenuItem onClick={preventDefaultStopPropogation(linkToNewPlaylist)}>
        {Constants.NewPlaylistName}
      </FadilaMenuItem>
    </FadilaSubMenu>
  );
}
